/**
 * The drawer beside the video stage: who is here, who is waiting at the
 * door (with the Admit / Deny buttons, colleagues only), the chat everyone
 * in the room shares, and — for colleagues — the transcript growing as
 * people talk. Guests get the people list and the chat and nothing else; the
 * transcript is the company's notes, not the visitor's.
 *
 * On a phone it covers the stage, so its header carries a back arrow: the
 * way out of a full-screen panel must be where the thumb expects it.
 *
 * Colleagues get moderation on each remote row — mute, camera off and, for
 * the host, remove. Mute and camera off are one tap: the person can switch
 * back on, so a slip costs nothing. Removing asks once, inline, because it
 * cannot be taken back from this side.
 */
import {
  ArrowLeft,
  Captions,
  Check,
  MessageSquare,
  MicOff,
  MonitorUp,
  Users,
  UserX,
  VideoOff,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import type { MeetMember } from '../../hooks/useMeetRoom';
import type { MeetChatMessage, MeetModeration, MeetParticipant } from '../../lib/meetApi';
import type { VoiceTranscriptSegment } from '../../lib/voiceCallsApi';
import { Avatar } from '../tasks/linear/Avatar';
import { VoiceTranscript } from '../teamChat/VoiceTranscript';
import { MeetChatPanel } from './MeetChatPanel';
import type { MeetPanel } from './MeetControls';

export function MeetSidePanel({
  panel,
  members,
  speakingIds,
  waiting,
  canAdmit,
  onAdmit,
  onDeny,
  canModerate,
  canRemove,
  onModerate,
  segments,
  meParticipantId,
  transcriptionEnabled,
  transcriptStopped,
  chat,
  chatSending,
  chatError,
  onSendChat,
  onClose,
}: {
  panel: Exclude<MeetPanel, null>;
  members: MeetMember[];
  speakingIds: string[];
  waiting: MeetParticipant[];
  canAdmit: boolean;
  onAdmit: (participantId: string) => void;
  onDeny: (participantId: string) => void;
  /** May mute others and turn their cameras off (colleagues). */
  canModerate: boolean;
  /** May put someone out of the meeting (the host). */
  canRemove: boolean;
  onModerate: (participantId: string, action: MeetModeration) => void;
  segments: VoiceTranscriptSegment[];
  meParticipantId: string | null;
  transcriptionEnabled: boolean;
  transcriptStopped: string | null;
  chat: MeetChatMessage[];
  chatSending: boolean;
  chatError: string | null;
  onSendChat: (text: string) => Promise<boolean>;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const speaking = new Set(speakingIds);
  // The one row whose "remove?" question is open; a tap elsewhere closes it.
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  return (
    <aside className="flex h-full w-full flex-col border-l border-border bg-card text-card-foreground md:w-80 lg:w-96">
      <header className="flex items-center gap-2 border-b border-border px-3 py-3 pt-[calc(0.75rem+env(safe-area-inset-top))] md:pt-3">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.back')}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground md:hidden"
        >
          <ArrowLeft size={18} />
        </button>
        {panel === 'people' ? (
          <Users size={16} />
        ) : panel === 'chat' ? (
          <MessageSquare size={16} />
        ) : (
          <Captions size={16} />
        )}
        <h2 className="flex-1 text-sm font-semibold">
          {panel === 'people'
            ? t('meet.controls.people')
            : panel === 'chat'
              ? t('meet.controls.chat')
              : t('meet.controls.transcript')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="hidden rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground md:block"
        >
          <X size={16} />
        </button>
      </header>

      {panel === 'chat' ? (
        // The chat owns its scrolling: the list follows the newest line and
        // the composer stays pinned under it.
        <MeetChatPanel
          messages={chat}
          meParticipantId={meParticipantId ?? ''}
          sending={chatSending}
          error={chatError}
          onSend={onSendChat}
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {panel === 'people' ? (
            <div className="space-y-5">
              {canAdmit && waiting.length > 0 && (
                <section>
                  <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-amber-400">
                    {t('meet.waitingRoom')} · {waiting.length}
                  </h3>
                  <ul className="space-y-2">
                    {waiting.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-2"
                      >
                        <Avatar name={p.displayName} size={26} />
                        <span className="min-w-0 flex-1 truncate text-sm">{p.displayName}</span>
                        <button
                          type="button"
                          onClick={() => onAdmit(p.id)}
                          title={t('meet.admit')}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-fixed-white hover:bg-emerald-700"
                        >
                          <Check size={13} />
                          {t('meet.admit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeny(p.id)}
                          title={t('meet.deny')}
                          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-red-400"
                        >
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              <section>
                <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                  {t('meet.inMeeting')} · {members.length}
                </h3>
                <ul className="space-y-1.5">
                  {members.map((m) => {
                    const moderated = !m.isLocal && (canModerate || canRemove);
                    const asking = confirmRemove === m.id;
                    return (
                      <li
                        key={m.id}
                        className="group flex items-center gap-2 rounded-md px-1 py-1 hover:bg-accent/40"
                      >
                        <span
                          className={[
                            'rounded-full ring-2',
                            speaking.has(m.id) ? 'ring-emerald-400' : 'ring-transparent',
                          ].join(' ')}
                        >
                          <Avatar name={m.name} size={26} />
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm">
                          {m.isLocal ? t('meet.you') : m.name}
                        </span>
                        {asking ? (
                          <span className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground">
                              {t('meet.moderate.removeAsk')}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setConfirmRemove(null);
                                onModerate(m.id, 'remove');
                              }}
                              className="rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-fixed-white hover:bg-red-700"
                            >
                              {t('meet.moderate.removeConfirm')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmRemove(null)}
                              aria-label={t('common.cancel')}
                              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                            >
                              <X size={14} />
                            </button>
                          </span>
                        ) : (
                          <>
                            {m.sharingScreen && (
                              <MonitorUp size={14} className="text-emerald-400" />
                            )}
                            {moderated ? (
                              <span className="flex items-center gap-0.5">
                                {canModerate && (
                                  <button
                                    type="button"
                                    disabled={!m.micOn}
                                    onClick={() => onModerate(m.id, 'mute')}
                                    title={
                                      m.micOn
                                        ? t('meet.moderate.mute')
                                        : t('meet.moderate.alreadyMuted')
                                    }
                                    aria-label={t('meet.moderate.mute')}
                                    className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent"
                                  >
                                    <MicOff size={14} />
                                  </button>
                                )}
                                {canModerate && (
                                  <button
                                    type="button"
                                    disabled={!m.camOn}
                                    onClick={() => onModerate(m.id, 'camera-off')}
                                    title={
                                      m.camOn
                                        ? t('meet.moderate.cameraOff')
                                        : t('meet.moderate.cameraAlreadyOff')
                                    }
                                    aria-label={t('meet.moderate.cameraOff')}
                                    className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent"
                                  >
                                    <VideoOff size={14} />
                                  </button>
                                )}
                                {canRemove && (
                                  <button
                                    type="button"
                                    onClick={() => setConfirmRemove(m.id)}
                                    title={t('meet.moderate.remove')}
                                    aria-label={t('meet.moderate.remove')}
                                    className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-red-400"
                                  >
                                    <UserX size={14} />
                                  </button>
                                )}
                              </span>
                            ) : (
                              !m.micOn && <MicOff size={14} className="text-muted-foreground" />
                            )}
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>
          ) : (
            <div>
              {!transcriptionEnabled && (
                <p className="mb-3 text-sm text-muted-foreground">{t('meet.transcript.off')}</p>
              )}
              {transcriptStopped && (
                <p className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
                  {t(transcriptStopped)}
                </p>
              )}
              <VoiceTranscript
                segments={segments}
                meParticipantId={meParticipantId ?? undefined}
                followLatest
                emptyText={t('meet.transcript.empty')}
              />
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
